import {TextField} from '@hconnect/common/components'
import {CardBox} from '@hconnect/uikit/src/lib2'
import {Stack} from '@mui/material'
import {useFormContext} from 'react-hook-form'

import {
  ChecklistStepSection,
  TextFieldGroupWithRetake,
  YesNoWithCommentField
} from '../../components'
import {HROC_CHECKLIST_FORM_WIDTH_IN_PX} from '../../consts'
import {useWidgetTranslation} from '../../hooks'
import type {KilnCheckSheetKilnSectionFormValues} from '../../types'
import {StepFormActionsContainer} from '../StepFormActionsContainer'

type Props = {
  readOnly?: boolean
}

export const KilnSectionFields = ({readOnly = false}: Props) => {
  const {control} = useFormContext<KilnCheckSheetKilnSectionFormValues>()
  const {t} = useWidgetTranslation()

  return (
    <CardBox width={{xs: '100%', md: HROC_CHECKLIST_FORM_WIDTH_IN_PX}}>
      <Stack spacing={1}>
        <ChecklistStepSection
          subTitle={t('kilnCheckSheet.label.increaseFrequencyIfCreepBelow')}
          title={t('kilnCheckSheet.label.slippageCreepChecks')}
        >
          <Stack spacing={1}>
            <TextFieldGroupWithRetake
              fields={[
                {
                  name: 'slippageCreepChecks1st',
                  label: t('kilnCheckSheet.label.slippageCreepChecks1st'),
                  retakeName: 'slippageCreepChecks1stRetake'
                },
                {
                  name: 'slippageCreepChecks2nd',
                  label: t('kilnCheckSheet.label.slippageCreepChecks2nd'),
                  retakeName: 'slippageCreepChecks2ndRetake'
                },
                {
                  name: 'slippageCreepChecks3rd',
                  label: t('kilnCheckSheet.label.slippageCreepChecks3rd'),
                  retakeName: 'slippageCreepChecks3rdRetake'
                }
              ]}
              commentField={{
                name: 'slippageCreepChecksComment',
                label: t('kilnCheckSheet.label.slippageCreepChecksComment')
              }}
              control={control}
              readOnly={readOnly}
            />
          </Stack>
        </ChecklistStepSection>
        <ChecklistStepSection title={t('kilnCheckSheet.label.kilnBearingsOilLevel')}>
          <YesNoWithCommentField
            label={t('kilnCheckSheet.label.kilnBearingsOilLevelLevelsOk')}
            name="kilnBearingsOilLevelLevelsOk"
            control={control}
            readOnly={readOnly}
          />
        </ChecklistStepSection>
        <ChecklistStepSection title={t('kilnCheckSheet.label.kilnBearingsCoolingWaterFlow')}>
          <YesNoWithCommentField
            label={t('kilnCheckSheet.label.kilnBearingsCoolingWaterFlowOk')}
            name="kilnBearingsCoolingWaterFlowSufficientFlow"
            control={control}
            readOnly={readOnly}
          />
        </ChecklistStepSection>
        <ChecklistStepSection title={t('kilnCheckSheet.label.kilnThrustRollerOilLevel')}>
          <YesNoWithCommentField
            label={t('kilnCheckSheet.label.kilnThrustRollerOilLevelOk')}
            name="kilnThrustRollerOilLevelLevelsOk"
            control={control}
            readOnly={readOnly}
          />
        </ChecklistStepSection>
        <ChecklistStepSection title={t('kilnCheckSheet.label.kilnThrustRollerKilnPosition')}>
          <Stack direction="row" spacing={1}>
            <TextField
              fullWidth
              name="kilnThrustRollerKilnPositionOnThrust"
              label={t('kilnCheckSheet.label.kilnThrustRollerKilnPositionOnThrust')}
              control={control}
              readOnly={readOnly}
            />
            <TextField
              fullWidth
              name="kilnThrustRollerKilnPositionFloating"
              label={t('kilnCheckSheet.label.kilnThrustRollerKilnPositionFloating')}
              control={control}
              readOnly={readOnly}
            />
            <TextField
              fullWidth
              name="kilnThrustRollerKilnPositionUphill"
              label={t('kilnCheckSheet.label.kilnThrustRollerKilnPositionUphill')}
              control={control}
              readOnly={readOnly}
            />
          </Stack>
          <Stack>
            <TextField
              name="kilnThrustRollerKilnPositionComment"
              label={t('kilnCheckSheet.label.kilnThrustRollerKilnPositionComment')}
              control={control}
              readOnly={readOnly}
            />
          </Stack>
        </ChecklistStepSection>
        <ChecklistStepSection title={t('kilnCheckSheet.label.kilnFeedEndSealPlatesBoltsCables')}>
          <Stack direction="row" spacing={1}>
            <TextField
              fullWidth
              name="kilnFeedEndSealPlatesBoltsCablesNormal"
              label={t('kilnCheckSheet.label.kilnFeedEndSealPlatesBoltsCablesNormal')}
              control={control}
              readOnly={readOnly}
            />
            <TextField
              fullWidth
              name="kilnFeedEndSealPlatesBoltsCablesDeficiency"
              label={t('kilnCheckSheet.label.kilnFeedEndSealPlatesBoltsCablesDeficiency')}
              control={control}
              readOnly={readOnly}
            />
          </Stack>
          <Stack>
            <TextField
              name="kilnFeedEndSealPlatesBoltsCablesComment"
              label={t('kilnCheckSheet.label.kilnFeedEndSealPlatesBoltsCablesComment')}
              control={control}
              readOnly={readOnly}
            />
          </Stack>
        </ChecklistStepSection>
        <ChecklistStepSection title={t('kilnCheckSheet.label.kilnFeedEndSealMaterialLeak')}>
          <YesNoWithCommentField
            label={t('kilnCheckSheet.label.kilnFeedEndSealMaterialLeakOk')}
            name="kilnFeedEndSealMaterialLeak"
            control={control}
            readOnly={readOnly}
          />
        </ChecklistStepSection>
        <ChecklistStepSection title={t('kilnCheckSheet.label.kilnDischargeSealPlatesCablesShroud')}>
          <Stack direction="row" spacing={1}>
            <TextField
              fullWidth
              name="kilnDischargeSealPlatesCablesShroudNormal"
              label={t('kilnCheckSheet.label.kilnDischargeSealPlatesCablesShroudNormal')}
              control={control}
              readOnly={readOnly}
            />
            <TextField
              fullWidth
              name="kilnDischargeSealPlatesCablesShroudDamaged"
              label={t('kilnCheckSheet.label.kilnDischargeSealPlatesCablesShroudDamaged')}
              control={control}
              readOnly={readOnly}
            />
          </Stack>
          <Stack>
            <TextField
              name="kilnDischargeSealPlatesCablesShroudComment"
              label={t('kilnCheckSheet.label.kilnDischargeSealPlatesCablesShroudComment')}
              control={control}
              readOnly={readOnly}
            />
          </Stack>
        </ChecklistStepSection>
        <ChecklistStepSection
          title={t('kilnCheckSheet.label.kilnDischargeSealMaterialLeakExcessiveSpillage')}
        >
          <YesNoWithCommentField
            label={t('kilnCheckSheet.label.kilnDischargeSealMaterialLeakExcessiveSpillageOk')}
            name="kilnDischargeSealMaterialLeakExcessiveSpillage"
            control={control}
            readOnly={readOnly}
          />
        </ChecklistStepSection>
        <ChecklistStepSection title={t('kilnCheckSheet.label.noseRingNoseCastings')}>
          <Stack direction="row" spacing={1}>
            <TextField
              fullWidth
              name="noseRingNoseCastingsNormal"
              label={t('kilnCheckSheet.label.noseRingNoseCastingsNormal')}
              control={control}
              readOnly={readOnly}
            />
            <TextField
              fullWidth
              name="noseRingNoseCastingsDamaged"
              label={t('kilnCheckSheet.label.noseRingNoseCastingsDamaged')}
              control={control}
              readOnly={readOnly}
            />
          </Stack>
          <Stack>
            <TextField
              name="noseRingNoseCastingsComment"
              label={t('kilnCheckSheet.label.noseRingNoseCastingsComment')}
              control={control}
              readOnly={readOnly}
            />
          </Stack>
        </ChecklistStepSection>
        <ChecklistStepSection title={t('kilnCheckSheet.label.noseRingNoseRingFans')}>
          <YesNoWithCommentField
            label={t('kilnCheckSheet.label.noseRingNoseRingFansOperatingProperly')}
            name="noseRingNoseRingFansOperatingProperly"
            control={control}
            readOnly={readOnly}
          />
        </ChecklistStepSection>
        <ChecklistStepSection title={t('kilnCheckSheet.label.noseRingFansGreased3ShotsOfGrease')}>
          <YesNoWithCommentField
            label={t('kilnCheckSheet.label.noseRingFansGreased3ShotsOfGreaseOk')}
            name="noseRingFansGreased3ShotsOfGrease"
            control={control}
            readOnly={readOnly}
          />
        </ChecklistStepSection>
        <ChecklistStepSection title={t('kilnCheckSheet.label.shellFansBeltsBearingsMotor')}>
          <YesNoWithCommentField
            label={t('kilnCheckSheet.label.shellFansBeltsBearingsMotorOperatingProperlyOk')}
            name="shellFansBeltsBearingsMotorOperatingProperly"
            control={control}
            readOnly={readOnly}
          />
        </ChecklistStepSection>
        <StepFormActionsContainer />
      </Stack>
    </CardBox>
  )
}
